import React from 'react'
import classNames from 'classnames'
import isEmpty from 'lodash-es/isEmpty'

import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core/styles'

import { IQuizQuestion } from 'protos'

import Grid from '@material-ui/core/Grid'
import CloseIcon from '@material-ui/icons/Close'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'

import T from 'components/FormattedMessage'
import QuizChoices from 'components/QuizChoices'
import QuizProgressBar from 'components/QuizProgressBar'
import MarkdownTypography from 'components/MarkdownTypography'
import { QuizAnswersContext } from 'contexts/QuizAnswersContext'

import GameCardButton from '../GameCardButton'
import GameCardHeader from '../GameCardHeader'
import GameCardContent from '../GameCardContent'

import shield from './assets/shield.png'

interface Props {
  onClose?: () => (Promise<void> | void),
  onAnswer?: (gameId: string, questionId: string, answerId: string) => Promise<void>,
  gameId: string,
  homeTeam: string,
  awayTeam: string,
  questions: IQuizQuestion[],
  readonly?: boolean,
  className?: string,
}

interface State {
  error?: Error,
  answers: { [key: string]: string },
  isLoading: boolean,
  activeQuestionIndex: number,
}

const styles = (theme: Theme) => createStyles({
  root: {
    filter: 'drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1))',
  },
  content: {
    padding: theme.spacing(),
    position: 'relative',
  },
  close: {
    top: 0,
    right: 0,
    position: 'absolute',
  },
  closeIcon: {
    color: theme.palette.grey[500],
    width: 12,
    height: 12,
  },
  image: {
    height: 100,
  },
  title: {
    lineHeight: 1.2,
    marginBottom: theme.spacing(),
  },
  subtitle: {
    color: theme.palette.grey[500],
  },
})

type InjectedProps = Props & WithStyles<typeof styles>

type Action =
  | { type: 'sent' }
  | { type: 'next' }
  | { type: 'sending' }
  | { type: 'answer', questionId: string, answerId: string }
  | { type: 'error', error: Error, questionId: string }

const createInitialState = (questions: IQuizQuestion[], readonly?: boolean): State => {
  const initialState: State = { answers: {}, isLoading: false, activeQuestionIndex: 0 }

  for (const question of questions) {
    if (question.selectedAnswerId) {
      initialState.answers[question.questionId!] = question.selectedAnswerId!
      initialState.activeQuestionIndex++
    }
  }

  if (readonly || initialState.activeQuestionIndex === questions.length) {
    initialState.activeQuestionIndex = 0
  }

  return initialState
}

const reducer: React.Reducer<State, Action> = (state, action) => {
  switch (action.type) {
    case 'next':
      return { ...state, activeQuestionIndex: state.activeQuestionIndex + 1 }
    case 'sent':
      return { ...state, isLoading: false }
    case 'sending':
      return { ...state, isLoading: true, error: undefined }
    case 'answer':
      return { ...state, isLoading: false, answers: { ...state.answers, [action.questionId]: action.answerId } }
    case 'error':
      delete state.answers[action.questionId]
      return { ...state, isLoading: false, error: action.error }
    default:
      throw new Error(`Invalid QuizActive reducer action (${action})!`)
  }
}

const QuizCard: React.SFC<InjectedProps> = ({
  classes,
  onClose,
  onAnswer,
  gameId,
  homeTeam,
  awayTeam,
  questions,
  readonly,
  className,
  ...props
}) => {
  const { getSelectedAnswerId, persistAnswer } = React.useContext(QuizAnswersContext)

  const [{
    error,
    answers,
    isLoading,
    activeQuestionIndex,
  }, dispatch] = React.useReducer(reducer, createInitialState(questions.map((question) => {
    return {
      ...question,
      selectedAnswerId: getSelectedAnswerId(gameId, question.questionId!) || question.selectedAnswerId,
    }
  }), readonly))

  const activeQuestion = questions[activeQuestionIndex]
  const totalQuestions = questions.filter((question) => !isEmpty(question.answer)).length
  const isLastQuestion = activeQuestionIndex === totalQuestions - 1
  const answeredQuestions = readonly ? activeQuestionIndex + 1 : Object.keys(answers).length
  const isButtonDisabled = !Boolean(answers[activeQuestion.questionId!]) || isLoading
  const buttonAction = isLastQuestion ? onClose : () => dispatch({ type: 'next' })
  const buttonLabel = isLoading
    ? { id: 'quiz_layer_busybutton', defaultMessage: 'Saving your answer...' }
    : isLastQuestion
      ? { id: 'quiz_layer_finishbutton', defaultMessage: 'Finish' }
      : { id: 'quiz_layer_nextbutton', defaultMessage: 'Next Question' }
  const progressColor = readonly ? 'secondary' : 'primary'
  const selectedColor = activeQuestion.editable
    ? 'primary'
    : activeQuestion.correctAnswerId === activeQuestion.selectedAnswerId ? 'primary' : 'error'

  const onAnswerChange = async (answerId: string) => {
    if (isLoading) {
      return
    }

    const questionId = activeQuestion.questionId!

    dispatch({ type: 'answer', questionId, answerId })

    if (onAnswer) {
      try {
        dispatch({ type: 'sending' })
        await onAnswer(gameId, activeQuestion.questionId!, answerId)
        persistAnswer(gameId, activeQuestion.questionId!, answerId)
        dispatch({ type: 'sent' })
      } catch (error) {
        dispatch({ type: 'error', error, questionId })
      }
    }
  }

  return (
    <div className={classNames(className, classes.root)}>
      <GameCardHeader homeTeam={homeTeam} awayTeam={awayTeam} separator={` : `} />
      <GameCardContent className={classes.content} variant="quiz" separated>
        <IconButton onClick={onClose} className={classes.close}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>

        <Grid alignItems="center" container>
          <Grid item>
            <img src={activeQuestion.iconUrl || shield} className={classes.image} alt="Quiz Question Logo" />
          </Grid>
          <Grid xs item>
            <MarkdownTypography
              lexer="inline"
              variant="h6"
              markdown={activeQuestion.title}
              className={classes.title}
            />
            <MarkdownTypography
              lexer="inline"
              markdown={activeQuestion.subtitle}
              className={classes.subtitle}
            />
          </Grid>
        </Grid>

        <QuizChoices
          answers={activeQuestion.answer!}
          readOnly={!activeQuestion.editable || readonly}
          selectedColor={selectedColor}
          onAnswerChange={readonly ? undefined : onAnswerChange}
          selectedAnswerId={answers[activeQuestion.questionId!]}
        />

        <QuizProgressBar
          color={progressColor}
          totalQuestions={totalQuestions}
          currentQuestion={activeQuestionIndex + 1}
          answeredQuestions={answeredQuestions}
        />

        {error && (
          <Grid alignItems="center" justify="center" container>
            <Grid item>
              <Typography color="error" align="center">
                <T id="quiz_layer_error" defaultMessage="can't save your answer" />
              </Typography>
            </Grid>
          </Grid>
        )}
      </GameCardContent>

      <GameCardButton
        color="neutral"
        label={<T {...buttonLabel} />}
        onClick={buttonAction}
        disabled={!readonly && isButtonDisabled}
        clipped
      />
    </div>
  )
}

export default React.memo(withStyles(styles)(QuizCard))
